import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { runQaAnalysis } from 'api/platform';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import {
  getOrchestrationKey,
  getResultNameTypesByOrchestrations,
  ResultNameOrchestrations,
} from 'interfaces/calculateFeatures';
import { PipsResultsConfig } from 'interfaces/inferenceArtifacts';
import { JobType, QaAnalysisJob } from 'interfaces/job';
import { MapArtifactToSlides } from 'interfaces/jobs/basic';
import { filter, flatMap, forEach, get, indexOf, isEmpty, isEqual, isNil, map, size } from 'lodash';
import { useSnackbar } from 'notistack';
import { default as React, ReactElement, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { humanize } from 'utils/helpers';
import { OrchestrationBySlideByResultName, useOrchestrationSelections } from 'utils/orchestrationSelections';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import * as yup from 'yup';
import { ActionStepper } from '../../ActionStepper';
import { JobWithRebuild } from '../../JobWithRebuild';
import { LoadFromPreviousJobStep } from '../../LoadFromPreviousJobStep';
import { usePreviousJobsAvailability } from '../../LoadFromPreviousJobStep/usePreviousJobsAvailability';
import InferenceModelsForSlides from '../../PostProcessingAndCalculateFeatures/InferenceModelsForSlides';
import { useArtifactResultsQuery } from '../../PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import {
  findByOrchestrationResultArtifactUrlPattern,
  findOrchestrationByArtifactUrlPattern,
} from '../../PostProcessingAndCalculateFeatures/utils';
import { getMapArtifactToSlides, JobNameAndDescriptionStep } from '../../utils';
import { MultiplexSelectionAlert } from '../MultiplexSelectionAlert';
import { useMultiplexActionSelection } from '../useMultiplexActionSelection';
import { AdditionalParametersStep } from './AdditionalParametersStep';
import { QaAnalysisFormValues, QaAnalysisJobConfig, runQaAnalysisOrder, RunQaAnalysisStep } from './types';

const SNACK_BAR_KEY_RUN_QA_ANALYSIS = 'RUN_QA_ANALYSIS';

export interface RunQaAnalysisProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  jobId?: string;
}

const defaultValues: QaAnalysisFormValues = {
  jobName: '',
  jobDescription: '',
  configParams: {
    pipsCellsTablesArtifactUrls: [],
    markers: [],
    assignmentIds: [],
    manifest: [],
  },
};

const validationSchema = {
  [RunQaAnalysisStep.LoadParams]: yup.object({}),
  [RunQaAnalysisStep.NameAndDescription]: yup.object({}),
  [RunQaAnalysisStep.SelectInferenceModels]: yup.object().shape({
    configParams: yup.object().shape({
      pipsCellsTablesArtifactUrls: yup
        .array()
        .of(
          yup.object().shape({
            artifactUrlPattern: yup.string().required('Artifact URL Pattern is required'),
            slideIds: yup
              .array()
              .of(yup.string().required('Slide ID is required'))
              .min(1, 'At least one Slide ID is required'),
          })
        )
        .min(1, 'At least one Artifact URL Pattern is required')
        .required('Artifact URL Pattern is required'),
    }),
  }),
  [RunQaAnalysisStep.AdditionalParameters]: yup.object({
    configParams: yup.object().shape({
      markers: yup
        .array()
        .of(yup.string().required('Marker name is required'))
        .min(1, 'At least one marker must be selected')
        .required('Marker selection is required'),
    }),
  }),
};

const getAliasedValue = <Value,>(record: unknown, aliases: string[], fallback: Value): Value => {
  for (const alias of aliases) {
    const value = get(record, alias);

    if (!isNil(value)) {
      return value as Value;
    }
  }

  return fallback;
};

const normalizeArtifactSelections = (artifactSelections: unknown): MapArtifactToSlides[] => {
  return filter(
    map((artifactSelections || []) as Record<string, unknown>[], (artifactSelection) => ({
      artifactUrlPattern: getAliasedValue<string>(
        artifactSelection,
        ['artifactUrlPattern', 'artifact_url_pattern'],
        ''
      ),
      slideIds: getAliasedValue<string[]>(artifactSelection, ['slideIds', 'slide_ids'], []),
    })),
    ({ artifactUrlPattern }) => !isEmpty(artifactUrlPattern)
  );
};

// Persisted QA analysis job params nest the user-facing selections under the flow's
// results-to-db result_type (`qa_analysis_results`), e.g. job.params.deps.qa_analysis_results.*,
// mirrored under job.params.config_set.db_dependencies.*. The remaining aliases cover the
// flatter request-style shapes used by other callers/older jobs.
const QA_ANALYSIS_PARAM_ALIASES = {
  pipsCellsTablesArtifactUrls: [
    'deps.qa_analysis_results.pips_cells_tables_artifact_urls',
    'config_set.db_dependencies.pips_cells_tables_artifact_urls',
    'pipsCellsTablesArtifactUrls',
    'pips_cells_tables_artifact_urls',
    'params.pipsCellsTablesArtifactUrls',
    'params.pips_cells_tables_artifact_urls',
    'configParams.pipsCellsTablesArtifactUrls',
    'config_params.pips_cells_tables_artifact_urls',
    'params.configParams.pipsCellsTablesArtifactUrls',
    'params.config_params.pips_cells_tables_artifact_urls',
  ],
  markers: [
    'deps.qa_analysis_results.markers',
    'config_set.db_dependencies.markers',
    'config_set.markers_to_include',
    'markers',
    'params.markers',
    'configParams.markers',
    'config_params.markers',
    'params.configParams.markers',
    'params.config_params.markers',
  ],
  assignmentIds: [
    'deps.qa_analysis_results.assignment_ids',
    'config_set.db_dependencies.assignment_ids',
    'config_set.assignment_ids',
    'assignmentIds',
    'assignment_ids',
    'params.assignmentIds',
    'params.assignment_ids',
    'configParams.assignmentIds',
    'config_params.assignment_ids',
    'params.configParams.assignmentIds',
    'params.config_params.assignment_ids',
  ],
  manifest: [
    'deps.qa_analysis_results.manifest',
    'config_set.db_dependencies.manifest',
    'manifest',
    'params.manifest',
    'configParams.manifest',
    'config_params.manifest',
    'params.configParams.manifest',
    'params.config_params.manifest',
  ],
};

const getQaAnalysisFormValuesFromJob = (job?: Partial<QaAnalysisJob>): QaAnalysisFormValues => {
  const pipsCellsTablesArtifactUrls = normalizeArtifactSelections(
    getAliasedValue<unknown>(job?.params, QA_ANALYSIS_PARAM_ALIASES.pipsCellsTablesArtifactUrls, [])
  );

  return {
    jobName: job?.name || '',
    jobDescription: job?.description || '',
    configParams: {
      pipsCellsTablesArtifactUrls,
      markers: getAliasedValue<string[]>(job?.params, QA_ANALYSIS_PARAM_ALIASES.markers, []),
      assignmentIds: map(getAliasedValue<unknown[]>(job?.params, QA_ANALYSIS_PARAM_ALIASES.assignmentIds, []), Number),
      manifest: getAliasedValue<string[]>(job?.params, QA_ANALYSIS_PARAM_ALIASES.manifest, job?.manifest || []),
    },
  };
};

const buildSelectedPipsOrchestrationsFromJob = (
  pipsCellsTablesArtifactUrls: MapArtifactToSlides[],
  pipsResultsArtifacts: ResultNameOrchestrations[]
): OrchestrationBySlideByResultName => {
  const selectedPipsArtifacts: OrchestrationBySlideByResultName = {};

  forEach(pipsCellsTablesArtifactUrls, ({ artifactUrlPattern, slideIds }) => {
    const resultGroup = findByOrchestrationResultArtifactUrlPattern(pipsResultsArtifacts, artifactUrlPattern);
    const orchestration = findOrchestrationByArtifactUrlPattern(resultGroup?.orchestrations, artifactUrlPattern);

    if (!resultGroup || !orchestration) {
      return;
    }

    const resultKey = getOrchestrationKey({
      resultName: resultGroup.resultName,
      resultType: resultGroup.resultType,
    });

    forEach(slideIds, (slideId) => {
      selectedPipsArtifacts[slideId] = {
        ...selectedPipsArtifacts[slideId],
        [resultKey]: {
          orchestration,
        },
      };
    });
  });

  return selectedPipsArtifacts;
};

export const RunQaAnalysis = (props: RunQaAnalysisProps): ReactElement => {
  const { onClose, casesParams, jobId } = props;
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const studyId = multiplexCasesParams.filters?.studyId;
  const orchestrationSelectionsState = useOrchestrationSelections();
  const { selectedPipsOrchestrations, setSelectedPipsOrchestrations } = orchestrationSelectionsState;
  const [selectedJobId, setSelectedJobId] = useState<string>(undefined);
  const [isLoadingSelectedPreviousJob, setIsLoadingSelectedPreviousJob] = useState(false);
  const [pendingPreviousJobPipsSelections, setPendingPreviousJobPipsSelections] = useState<
    QaAnalysisJobConfig['pipsCellsTablesArtifactUrls'] | null
  >(null);
  const [preservePreviousJobPipsValuesOnNextSync, setPreservePreviousJobPipsValuesOnNextSync] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const [stepWarnings, setStepWarnings] = useState<Record<number, string>>({});

  const currentValidationSchema = validationSchema[runQaAnalysisOrder[activeStep]];

  const methods = useForm<QaAnalysisFormValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues: defaultValues,
  });
  useTrackActionFormDirty(methods.formState.isDirty);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    watch,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = methods;

  const runQaAnalysisMutation = useMutationWithErrorSnackbar({
    mutationFn: runQaAnalysis,
    mutationDescription: 'run QA analysis',
    onSuccess: () => {
      enqueueSnackbar('QA analysis started', { variant: 'success' });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_QA_ANALYSIS);
    },
  });

  const checkValidationAndSetIsStepFailed = async (step: RunQaAnalysisStep) => {
    const stepIndex = indexOf(runQaAnalysisOrder, step);
    let isValid = await trigger();

    setIsStepFailed((prev) => ({
      ...prev,
      [stepIndex]: !isValid,
    }));

    return isValid;
  };

  const setWarnings = (step: RunQaAnalysisStep) => {
    const stepIndex = indexOf(runQaAnalysisOrder, step);

    if (step === RunQaAnalysisStep.SelectInferenceModels) {
      setStepWarnings((prev) => ({
        ...prev,
        [stepIndex]:
          size(watch('configParams.manifest')) > 0 && size(watch('configParams.manifest')) < size(slides)
            ? 'Some selected slides do not have Post Processing - Cell Typing results selected, so QA analysis will skip them.'
            : '',
      }));
    }
  };

  const {
    data: pipsResultsArtifacts,
    isLoading: isPipsResultsLoading,
    isFetching: isPipsResultsFetching,
    isError: isPipsResultsError,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: multiplexCasesParams,
    config: PipsResultsConfig,
  });

  const { data: slides, isFetching: isSlidesFetching } = useQuery({
    queryKey: ['slidesDataForArtifactsResults', multiplexCasesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(multiplexCasesParams, signal),
    enabled: Boolean(studyId) && Boolean(effectiveCasesParams),
  });

  useEffect(() => {
    if (pendingPreviousJobPipsSelections === null || isPipsResultsLoading || isPipsResultsFetching) {
      return;
    }

    setSelectedPipsOrchestrations(
      buildSelectedPipsOrchestrationsFromJob(pendingPreviousJobPipsSelections, pipsResultsArtifacts || [])
    );
    setPendingPreviousJobPipsSelections(null);
  }, [
    isPipsResultsFetching,
    isPipsResultsLoading,
    pendingPreviousJobPipsSelections,
    pipsResultsArtifacts,
    setSelectedPipsOrchestrations,
  ]);

  useEffect(() => {
    if (pendingPreviousJobPipsSelections !== null) {
      return;
    }

    const inferenceArtifactUrlPatternsWithSlides: MapArtifactToSlides[] = getMapArtifactToSlides(
      map(slides, 'slideId'),
      selectedPipsOrchestrations
    );
    const currentArtifactUrlPatternsWithSlides = getValues('configParams.pipsCellsTablesArtifactUrls') || [];
    const nextManifest = flatMap(inferenceArtifactUrlPatternsWithSlides, 'slideIds');

    if (
      preservePreviousJobPipsValuesOnNextSync &&
      isEmpty(inferenceArtifactUrlPatternsWithSlides) &&
      !isEmpty(currentArtifactUrlPatternsWithSlides)
    ) {
      setPreservePreviousJobPipsValuesOnNextSync(false);
      return;
    }

    if (!isEqual(currentArtifactUrlPatternsWithSlides, inferenceArtifactUrlPatternsWithSlides)) {
      setValue('configParams.pipsCellsTablesArtifactUrls', inferenceArtifactUrlPatternsWithSlides);
    }

    if (!isEqual(getValues('configParams.manifest') || [], nextManifest)) {
      setValue('configParams.manifest', nextManifest);
    }

    setPreservePreviousJobPipsValuesOnNextSync(false);
    trigger('configParams.pipsCellsTablesArtifactUrls');
    setWarnings(RunQaAnalysisStep.SelectInferenceModels);
  }, [
    getValues,
    pendingPreviousJobPipsSelections,
    preservePreviousJobPipsValuesOnNextSync,
    selectedPipsOrchestrations,
    slides,
    setValue,
    trigger,
  ]);

  const onSelectedJobParamChange = (newValue: QaAnalysisJob) => {
    if (isEmpty(newValue)) {
      setSelectedJobId(undefined);
      setPendingPreviousJobPipsSelections(null);
      setPreservePreviousJobPipsValuesOnNextSync(false);
      setSelectedPipsOrchestrations({});
      reset(defaultValues);
    } else {
      const previousJobFormValues = getQaAnalysisFormValuesFromJob(newValue);

      setSelectedJobId(newValue.id);
      setPendingPreviousJobPipsSelections(previousJobFormValues.configParams.pipsCellsTablesArtifactUrls);
      setPreservePreviousJobPipsValuesOnNextSync(true);
      reset(previousJobFormValues);
    }
  };

  const { hasPreviousJobs, isCheckingPreviousJobs } = usePreviousJobsAvailability(JobType.QaAnalysis);

  const stepsMap = {
    [RunQaAnalysisStep.LoadParams]: {
      label: 'Reuse previous job settings',
      optional: true,
      hidden: !isCheckingPreviousJobs && !hasPreviousJobs,
      isLoading: isLoadingSelectedPreviousJob,
      content: (
        <LoadFromPreviousJobStep
          jobType={JobType.QaAnalysis}
          onSelectedJob={onSelectedJobParamChange}
          onSelectedJobLoadingChange={setIsLoadingSelectedPreviousJob}
          selectedJobId={selectedJobId}
        />
      ),
    },
    [RunQaAnalysisStep.NameAndDescription]: {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
    [RunQaAnalysisStep.SelectInferenceModels]: {
      label: 'Select Post Processing - Cell Typing',
      subLabel: activeStep !== 2 && `${size(watch('configParams.manifest'))}/${size(slides)} results selected`,
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {isPipsResultsError && (
              <Typography color="error">Error fetching Post Processing - Cell Typing results</Typography>
            )}
            {get(errors, 'configParams.pipsCellsTablesArtifactUrls') && (
              <Typography color="error">
                {humanize(get(errors, 'configParams.pipsCellsTablesArtifactUrls')?.message)}
              </Typography>
            )}
            <InferenceModelsForSlides
              studyId={studyId}
              slides={slides}
              modelsType={getResultNameTypesByOrchestrations(pipsResultsArtifacts)}
              pipsResultsArtifacts={pipsResultsArtifacts}
              displayInferenceModels={false}
              displayPipsResults
              isLoading={isPipsResultsLoading || isPipsResultsFetching || isSlidesFetching}
              {...orchestrationSelectionsState}
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => {
        checkValidationAndSetIsStepFailed(RunQaAnalysisStep.SelectInferenceModels);
        setWarnings(RunQaAnalysisStep.SelectInferenceModels);
      },
    },
    [RunQaAnalysisStep.AdditionalParameters]: {
      label: 'Analysis settings',
      content: (
        <AdditionalParametersStep
          methods={methods}
          checkValidationAndSetIsStepFailed={() =>
            checkValidationAndSetIsStepFailed(RunQaAnalysisStep.AdditionalParameters)
          }
          casesParams={multiplexCasesParams}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunQaAnalysisStep.AdditionalParameters),
    },
  };

  const steps = map(runQaAnalysisOrder, (stepId) => stepsMap[stepId]);

  const onSubmit: SubmitHandler<any> = (data: QaAnalysisFormValues) => {
    runQaAnalysisMutation.mutate({
      ...multiplexCasesParams,
      ...data,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for QA analysis to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_QA_ANALYSIS,
      autoHideDuration: null,
    });
  };

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <JobWithRebuild jobId={jobId} onSelectedJobParamChange={onSelectedJobParamChange}>
      <Grid container direction="column" spacing={2}>
        {multiplexSelectionInfoMessage ? (
          <Grid item>
            <MultiplexSelectionAlert
              severity="info"
              title="This action runs on slides"
              message={multiplexSelectionInfoMessage}
            />
          </Grid>
        ) : null}
        <Grid item>
          <ActionStepper
            handleSubmit={handleSubmit(onSubmit)}
            steps={steps}
            setActiveStepForValidation={setActiveStep}
            isStepFailed={isStepFailed}
            isInitializing={isCheckingPreviousJobs}
            initializingText="Checking for previous jobs…"
            validateStep={() => checkValidationAndSetIsStepFailed(runQaAnalysisOrder[activeStep])}
            stepWarnings={stepWarnings}
          />
        </Grid>
      </Grid>
    </JobWithRebuild>
  );
};
